import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Sparkles, CreditCard, Lock } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLang } from "@/context/LanguageContext";
import { createCheckout } from "@/lib/api";

const PENDING_KEY = "riclassificatore.pending_export.v1";

export default function PaywallModal({ open, onOpenChange, kind = "pdf", exportPayload }) {
  const { t, lang } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(lang === "it" ? "Email non valida" : "Invalid email");
      return;
    }
    setLoading(true);
    try {
      // Stage the FULL export payload in localStorage so /payment/success can execute it after unlock
      const staged = { kind, payload: exportPayload || null, savedAt: Date.now() };
      try {
        localStorage.setItem(PENDING_KEY, JSON.stringify(staged));
      } catch (_e) {
        // localStorage quota exceeded — the payload is large. Fallback: proceed without payload; success page will just show "paid" without auto-download.
      }

      const origin = window.location.origin;
      const res = await createCheckout({ origin_url: origin, customer_email: email || undefined });
      localStorage.setItem("riclassificatore.last_session.v1", res.session_id);
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(lang === "it" ? "Errore Stripe: riprova" : "Stripe error: retry");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-white" data-testid="paywall-modal">
        <DialogHeader>
          <div className="mb-2 flex items-center gap-2">
            <div className="grid h-9 w-9 place-items-center rounded-md bg-amber-100 text-amber-700">
              <Lock className="h-4 w-4" strokeWidth={2} />
            </div>
            <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-widest text-amber-800">
              Premium Report
            </span>
          </div>
          <DialogTitle className="font-heading text-xl font-semibold text-slate-900">
            {lang === "it" ? "Sblocca l'export del report" : "Unlock the report export"}
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed text-slate-600">
            {lang === "it"
              ? "Il pagamento una tantum di €499 sblocca l'export completo (PDF + Excel) con cover letter, correttivi AI, roadmap 12 mesi e disclaimer legale."
              : "One-time €499 payment unlocks the full export (PDF + Excel) with cover letter, AI correctives, 12-month roadmap and legal disclaimer."}
          </DialogDescription>
        </DialogHeader>

        <div className="my-4 rounded-lg border border-slate-200 bg-slate-50/60 p-4">
          <div className="mb-3 flex items-baseline justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500">
                {lang === "it" ? "Prezzo una tantum" : "One-time price"}
              </div>
              <div className="mt-1 font-heading text-3xl font-semibold text-slate-900">€499</div>
            </div>
            <div className="text-right text-[11px] text-slate-500">
              <div className="inline-flex items-center gap-1">
                <ShieldCheck className="h-3 w-3" strokeWidth={2} /> Stripe secure
              </div>
              <div>{lang === "it" ? "IVA esclusa · pagamento test" : "VAT excluded · test mode"}</div>
            </div>
          </div>
          <ul className="space-y-1.5 text-xs text-slate-700">
            <li className="flex items-start gap-2"><Sparkles className="mt-0.5 h-3 w-3 text-amber-600" strokeWidth={2} /> PDF firmato Finch &amp; Partners</li>
            <li className="flex items-start gap-2"><Sparkles className="mt-0.5 h-3 w-3 text-amber-600" strokeWidth={2} /> {lang === "it" ? "Correttivi + Roadmap AI inclusi" : "Correctives + AI Roadmap included"}</li>
            <li className="flex items-start gap-2"><Sparkles className="mt-0.5 h-3 w-3 text-amber-600" strokeWidth={2} /> {lang === "it" ? "Excel multi-anno editabile" : "Editable multi-year Excel"}</li>
          </ul>
        </div>

        <div className="space-y-2">
          <Label htmlFor="paywall-email" className="text-xs font-medium text-slate-600">
            {lang === "it" ? "Email (facoltativa, per ricevuta Stripe)" : "Email (optional, for Stripe receipt)"}
          </Label>
          <Input
            id="paywall-email"
            data-testid="paywall-email-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="mario@studiorossi.it"
            className="h-10 bg-white"
          />
        </div>

        <DialogFooter className="mt-4 gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading} className="rounded-full">
            {lang === "it" ? "Annulla" : "Cancel"}
          </Button>
          <Button
            data-testid="paywall-checkout-btn"
            onClick={handleCheckout}
            disabled={loading}
            className="rounded-full bg-blue-700 text-white hover:bg-blue-800"
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CreditCard className="mr-2 h-4 w-4" strokeWidth={2} />}
            {lang === "it" ? "Paga €499 con Stripe" : "Pay €499 with Stripe"}
          </Button>
        </DialogFooter>

        <div className="border-t border-slate-100 pt-3 text-[10px] text-slate-400">
          {lang === "it"
            ? "Modalità test: usa la carta 4242 4242 4242 4242, qualsiasi data futura e CVC."
            : "Test mode: use card 4242 4242 4242 4242, any future date and CVC."}
        </div>
      </DialogContent>
    </Dialog>
  );
}
